import { Container, Title, Text, Stack, Paper, Group, Button, Badge, Divider, Box, Anchor } from '@mantine/core';
import { APP_NAME, DEVELOPER_NAME, PANEL_SITE } from '../lib/brand';
import { IconArrowLeft, IconFileText, IconShieldLock, IconCookie, IconUserX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getLegalDoc, LegalKey } from '../legal/content';
import { LanguageMenu } from '../components/LanguageMenu';
import { useAuth } from '../contexts/AuthContext';
import { useSupportEmail } from '../hooks/useSupportEmail';

const NAV: { key: LegalKey; path: string; icon: typeof IconFileText }[] = [
  { key: 'terms', path: '/terms', icon: IconFileText },
  { key: 'privacy', path: '/privacy', icon: IconShieldLock },
  { key: 'cookies', path: '/cookies', icon: IconCookie },
];

/** Qatordagi email va https:// havolalar (oxiridagi tinish belgisisiz). */
const HAVOLA_RE = /([^\s@()]+@[^\s@()]+\.[a-z]{2,})|(https:\/\/[^\s()]+[^\s().,;:])/gi;
/** Panelning o'z sahifasi (masalan `https://mataa.uz/delete-account`) — router ichida ochiladi. */
const ICHKI = `https://${PANEL_SITE}/`;

/**
 * Hujjat qatori. Email'lar (maktabniki ham, dasturchiniki ham) — `mailto:`, https
 * manzillar — havola; panelning o'z sahifalari router `Link` bilan (lokal va prod'da
 * bir xil ishlaydi). Matnning qolgan qismi o'zgarishsiz.
 */
function LegalLine({ text }: { text: string }) {
  const out: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(HAVOLA_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const [v, email] = m;
    if (email) out.push(<Anchor key={idx} href={`mailto:${v}`} inherit>{v}</Anchor>);
    else if (v.startsWith(ICHKI)) out.push(<Anchor key={idx} component={Link} to={`/${v.slice(ICHKI.length)}`} inherit>{v}</Anchor>);
    else out.push(<Anchor key={idx} href={v} target="_blank" rel="noopener noreferrer" inherit>{v}</Anchor>);
    last = idx + v.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}

/**
 * Huquqiy hujjat sahifasi (ochiq — login talab qilmaydi).
 * Foydalanish shartlari / Maxfiylik siyosati / Cookie siyosati.
 *
 * Aloqa email'i — JORIY sessiya maktabiniki (`useSupportEmail`). Login'dan oldin,
 * email kiritilmagan yoki backend eski (404) bo'lsa — `null`, kontakt qatorlari
 * neytral matn bilan chiqadi (platformaning umumiy manzili qo'yilmaydi).
 */
export default function Legal({ docKey }: { docKey: LegalKey }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data: supportEmail } = useSupportEmail();
  const contactEmail = supportEmail ?? null;
  const doc = getLegalDoc(i18n.language, docKey, contactEmail);

  return (
    <Box mih="100vh" bg="var(--mantine-color-body)" py="xl">
      <Container size="md">
        <Group justify="space-between" mb="lg">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(isAuthenticated ? '/' : '/login')}
          >
            {t('legal.back')}
          </Button>
          <LanguageMenu />
        </Group>

        {/* Hujjatlar orasida o'tish */}
        <Group gap="xs" mb="md" wrap="wrap">
          {NAV.map(({ key, path, icon: Icon }) => (
            <Button
              key={key}
              component={Link}
              to={path}
              size="xs"
              variant={key === docKey ? 'filled' : 'default'}
              leftSection={<Icon size={14} />}
            >
              {getLegalDoc(i18n.language, key, null).title}
            </Button>
          ))}
          <Button component={Link} to="/delete-account" size="xs" variant="default" leftSection={<IconUserX size={14} />}>
            {t('deleteAccount.navLabel')}
          </Button>
        </Group>

        <Paper p="xl" radius="md" withBorder>
          <Stack gap="xs" mb="md">
            <Title order={2}>{doc.title}</Title>
            <Badge variant="light" w="fit-content">
              {t('legal.updated')}: {doc.updated}
            </Badge>
          </Stack>

          <Text c="dimmed" mb="lg">{doc.intro}</Text>
          <Divider mb="lg" />

          <Stack gap="lg">
            {doc.sections.map((s) => (
              <Box key={s.title}>
                <Title order={4} mb={6}>{s.title}</Title>
                <Stack gap={6}>
                  {s.body.map((p, i) => (
                    <Text key={i} size="sm" style={{ lineHeight: 1.65 }}>
                      <LegalLine text={p} />
                    </Text>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>

        <Text ta="center" size="xs" c="dimmed" mt="lg">
          © {new Date().getFullYear()} {DEVELOPER_NAME} · {APP_NAME}
        </Text>
      </Container>
    </Box>
  );
}
